import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./configure-multi-input.css";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useEffect, useState } from "react";
import FieldSettings from "./field-settings";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

type ColumnItem = {
  column: number;
  row: number;
  label: string;
  definedName?: string;
  inputType: string;
  inputFormat?: string;
  answerOptions?: string[];
  responseRequired?: boolean;
};

export default function ConfigureMultiInput({
  definedNames,
  usedDefinedNames,
  multiInputData,
  showDialog,
  closeDialog,
  onSave,
}: any) {
  const [column1Width, setColumn1Width] = useState("");
  const [column2Width, setColumn2Width] = useState("");
  const [column3Width, setColumn3Width] = useState("");
  const [column1Cells, setColumn1Cells] = useState<ColumnItem[]>([]);
  const [column2Cells, setColumn2Cells] = useState<ColumnItem[]>([]);
  const [column3Cells, setColumn3Cells] = useState<ColumnItem[]>([]);
  const [selectedCell, setSelectedCell] = useState<ColumnItem | null>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedDefinedNames, setSelectedDefinedNames] = useState<string[]>(
    []
  );

  useEffect(() => {
    setColumn1Width("calc(100% - 162px)");
    setColumn2Width("80px");
    setColumn3Width("80px");
  }, []);

  useEffect(() => {
    if (column2Cells.length > 0 && column3Cells.length > 0) {
      setColumn1Width("calc(100%/3 + 4px)");
      setColumn2Width("calc(100%/3 + 2px)");
      setColumn3Width("calc(100%/3)");
    } else if (column2Cells.length > 0 && column3Cells.length === 0) {
      setColumn1Width("calc((100% - 80px)/2)");
      setColumn2Width("calc((100% - 82px)/2)");
      setColumn3Width("80px");
    } else if (column2Cells.length === 0 && column3Cells.length === 0) {
      setColumn1Width("calc(100% - 162px)");
      setColumn2Width("80px");
      setColumn3Width("80px");
    }
  }, [column2Cells, column3Cells]);

  useEffect(() => {
    if (multiInputData) {
      const column1: ColumnItem[] = [];
      const column2: ColumnItem[] = [];
      const column3: ColumnItem[] = [];
      const filterColumn1 = multiInputData
        .filter(
          (element: any) => element?.multiInputElementSettings?.column === 1
        )
        .sort(
          (a: any, b: any) =>
            a?.multiInputElementSettings?.row -
            b?.multiInputElementSettings?.row
        );
      const filterColumn2 = multiInputData
        .filter(
          (element: any) => element?.multiInputElementSettings?.column === 2
        )
        .sort(
          (a: any, b: any) =>
            a?.multiInputElementSettings?.row -
            b?.multiInputElementSettings?.row
        );
      const filterColumn3 = multiInputData
        .filter(
          (element: any) => element?.multiInputElementSettings?.column === 3
        )
        .sort(
          (a: any, b: any) =>
            a?.multiInputElementSettings?.row -
            b?.multiInputElementSettings?.row
        );
      filterColumn1.forEach((col: any) => {
        const data: ColumnItem = {
          column: 1,
          row: col.multiInputElementSettings.row,
          label: col.title,
          definedName: col.definedName,
          answerOptions: col.answerOptions,
          inputType: col.multiInputElementSettings.inputType,
          inputFormat: col.multiInputElementSettings.inputFormat,
          responseRequired: col.responseRequired,
        };
        column1.push(data);
      });
      filterColumn2.forEach((col: any) => {
        const data: ColumnItem = {
          column: 2,
          row: col.multiInputElementSettings.row,
          label: col.title,
          definedName: col.definedName,
          answerOptions: col.answerOptions,
          inputType: col.multiInputElementSettings.inputType,
          inputFormat: col.multiInputElementSettings.inputFormat,
          responseRequired: col.responseRequired,
        };
        column2.push(data);
      });
      filterColumn3.forEach((col: any) => {
        const data: ColumnItem = {
          column: 3,
          row: col.multiInputElementSettings.row,
          label: col.title,
          definedName: col.definedName,
          answerOptions: col.answerOptions,
          inputType: col.multiInputElementSettings.inputType,
          inputFormat: col.multiInputElementSettings.inputFormat,
          responseRequired: col.responseRequired,
        };
        column3.push(data);
      });
      setColumn1Cells(column1);
      setColumn2Cells(column2);
      setColumn3Cells(column3);
    }
  }, [multiInputData]);

  useEffect(() => {
    const definedNames: string[] = [];
    if (usedDefinedNames && usedDefinedNames.length > 0) {
      definedNames.push(...usedDefinedNames);
    }
    if (column1Cells.length > 0) {
      column1Cells.forEach((cell) => {
        if (cell.definedName) definedNames.push(cell.definedName);
      });
    }
    if (column2Cells.length > 0) {
      column2Cells.forEach((cell) => {
        if (cell.definedName) definedNames.push(cell.definedName);
      });
    }
    if (column3Cells.length > 0) {
      column3Cells.forEach((cell) => {
        if (cell.definedName) definedNames.push(cell.definedName);
      });
    }
    setSelectedDefinedNames(definedNames);
  }, [column1Cells, column2Cells, column3Cells, usedDefinedNames]);

  const onAddColumn1Click = () => {
    const newCell: ColumnItem = {
      column: 1,
      row: column1Cells.length + 1,
      label: "",
      definedName: "",
      inputType: "",
      inputFormat: "",
      answerOptions: [],
      responseRequired: false,
    };
    setColumn1Cells((prev) => [...prev, newCell]);
    setSelectedCell(newCell);
  };

  const onAddColumn2Click = () => {
    const newCell: ColumnItem = {
      column: 2,
      row: column2Cells.length + 1,
      label: "",
      definedName: "",
      inputType: "",
      inputFormat: "",
      answerOptions: [],
      responseRequired: false,
    };
    setColumn2Cells((prev) => [...prev, newCell]);
    setSelectedCell(newCell);
  };
  const onAddColumn3Click = () => {
    const newCell: ColumnItem = {
      column: 3,
      row: column3Cells.length + 1,
      label: "",
      definedName: "",
      inputType: "",
      inputFormat: "",
      answerOptions: [],
      responseRequired: false,
    };
    setColumn3Cells((prev) => [...prev, newCell]);
    setSelectedCell(newCell);
  };

  const onSaveSettings = (data: any) => {
    if (data?.column === 1) {
      column1Cells.forEach((cell) => {
        if (cell.row === data.row) {
          cell.label = data.label;
          cell.definedName = data.definedName;
          cell.inputType = data.inputType;
          cell.inputFormat = data.inputFormat;
          cell.answerOptions = data.answerOptions;
          cell.responseRequired = data.responseRequired;
        }
      });
      setColumn1Cells((prev) => [...column1Cells]);
    } else if (data?.column === 2) {
      column2Cells.forEach((cell) => {
        if (cell.row === data.row) {
          cell.label = data.label;
          cell.definedName = data.definedName;
          cell.inputType = data.inputType;
          cell.inputFormat = data.inputFormat;
          cell.answerOptions = data.answerOptions;
          cell.responseRequired = data.responseRequired;
        }
      });
      setColumn2Cells((prev) => [...column2Cells]);
    } else if (data?.column === 3) {
      column3Cells.forEach((cell) => {
        if (cell.row === data.row) {
          cell.label = data.label;
          cell.definedName = data.definedName;
          cell.inputType = data.inputType;
          cell.inputFormat = data.inputFormat;
          cell.answerOptions = data.answerOptions;
          cell.responseRequired = data.responseRequired;
        }
      });
      setColumn3Cells((prev) => [...column3Cells]);
    }
    setSelectedCell(null);
    setIsEdit(false);
  };

  const onItemClick = (column: any, index: number) => {
    setIsEdit(true);
    const filterCell = [
      ...column1Cells,
      ...column2Cells,
      ...column3Cells,
    ].filter((cell) => cell.column === column && cell.row === index + 1);
    if (filterCell.length > 0) {
      setSelectedCell(filterCell[0]);
    }
  };

  const onDeleteCell = (data: ColumnItem) => {
    if (data.column === 1) {
      const filterCell = column1Cells
        .filter((cell) => cell.row !== data.row)
        .map((cell, index) => ({ ...cell, row: index + 1 }));
      setColumn1Cells(filterCell);
    } else if (data.column === 2) {
      const filterCell = column2Cells
        .filter((cell) => cell.row !== data.row)
        .map((cell, index) => ({ ...cell, row: index + 1 }));
      setColumn2Cells(filterCell);
    } else if (data.column === 3) {
      const filterCell = column3Cells
        .filter((cell) => cell.row !== data.row)
        .map((cell, index) => ({ ...cell, row: index + 1 }));
      setColumn3Cells(filterCell);
    }
    setIsEdit(false);
    setSelectedCell(null);
  };

  const onSaveClick = () => {
    const childElements: any[] = [];
    column1Cells.forEach((cell) => {
      const data: any = {
        title: cell?.label,
        definedName: cell?.definedName,
        answerOptions: cell?.answerOptions ? cell?.answerOptions : [],
        responseRequired: cell?.responseRequired,
        multiInputElementSettings: {
          row: cell?.row,
          column: cell?.column,
          inputType: cell?.inputType,
        },
      };
      if (cell?.inputType === "OpenEnd") {
        data.multiInputElementSettings.inputFormat = cell?.inputFormat;
      }
      childElements.push(data);
    });
    column2Cells.forEach((cell) => {
      const data: any = {
        title: cell?.label,
        definedName: cell?.definedName,
        answerOptions: cell?.answerOptions ? cell?.answerOptions : [],
        responseRequired: cell?.responseRequired,
        multiInputElementSettings: {
          row: cell?.row,
          column: cell?.column,
          inputType: cell?.inputType,
        },
      };
      if (cell?.inputType === "OpenEnd") {
        data.multiInputElementSettings.inputFormat = cell?.inputFormat;
      }
      childElements.push(data);
    });
    column3Cells.forEach((cell) => {
      const data: any = {
        title: cell?.label,
        definedName: cell?.definedName,
        answerOptions: cell?.answerOptions ? cell?.answerOptions : [],
        responseRequired: cell?.responseRequired,
        multiInputElementSettings: {
          row: cell?.row,
          column: cell?.column,
          inputType: cell?.inputType,
        },
      };
      if (cell?.inputType === "OpenEnd") {
        data.multiInputElementSettings.inputFormat = cell?.inputFormat;
      }
      childElements.push(data);
    });
    onSave(childElements);
    closeDialog();
  };

  return (
    <>
      <Dialog
        open={showDialog}
        onClose={closeDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth={true}
        maxWidth={"lg"}
      >
        <DialogTitle id="responsive-dialog-title">
          <Typography variant="h5" color="black">
            Configure Multi-Input
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          })}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description">
            <Box>
              <Typography mt={1}>
                Select a field or add a new one below
              </Typography>
              <Box className="columnLayout">
                <Typography
                  className="column"
                  sx={{
                    width: `calc(${column1Width} - 16px)`,
                  }}
                >
                  Column 1
                </Typography>
                <Typography
                  className="column3"
                  sx={{
                    width: `calc(${column2Width} - 16px)`,
                  }}
                >
                  Column 2
                </Typography>
                <Typography
                  className="column3"
                  sx={{
                    width: `calc(${column3Width} - 16px)`,
                  }}
                >
                  Column 3
                </Typography>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box
                  sx={{
                    width: column1Width,
                  }}
                >
                  {column1Cells.map((col1, index) => (
                    <Box
                      className="rowsColumn1"
                      onClick={() => {
                        onItemClick(1, index);
                      }}
                      sx={{
                        backgroundColor:
                          selectedCell?.column === 1 &&
                          selectedCell?.row === index + 1
                            ? "rgba(227, 95, 0, 0.08)"
                            : "none",
                      }}
                    >
                      <Typography
                        className="cellLabel"
                        sx={{ width: `calc(100% - 50px)` }}
                        noWrap
                      >
                        {col1.label}
                      </Typography>
                      <Typography
                        className="cellDefinedName"
                        sx={{ width: `calc(100% - 50px)` }}
                        noWrap
                      >
                        {col1.definedName ? `#${col1.definedName}` : ""}
                      </Typography>
                      {isEdit &&
                        selectedCell?.column === 1 &&
                        selectedCell?.row === index + 1 && (
                          <IconButton
                            sx={{ position: "absolute", right: 8, top: 8 }}
                            aria-label="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              onDeleteCell(col1);
                            }}
                          >
                            <DeleteOutlinedIcon color="error" />
                          </IconButton>
                        )}
                    </Box>
                  ))}
                  <IconButton
                    sx={{
                      backgroundColor: "rgb(277,95,0)",
                      m: 1,
                    }}
                    onClick={onAddColumn1Click}
                  >
                    <AddOutlinedIcon sx={{ color: "white" }} />
                  </IconButton>
                </Box>

                <Box sx={{ width: column2Width }}>
                  {column2Cells.map((col2, index) => (
                    <Box
                      className="rowsColumn2"
                      onClick={() => {
                        onItemClick(2, index);
                      }}
                      sx={{
                        backgroundColor:
                          selectedCell?.column === 2 &&
                          selectedCell?.row === index + 1
                            ? "rgba(227, 95, 0, 0.08)"
                            : "none",
                        borderLeft:
                          column1Cells.length < column2Cells.length &&
                          index >= column1Cells.length
                            ? "1px solid #c4c4c4"
                            : "none",
                      }}
                    >
                      <Typography
                        className="cellLabel"
                        sx={{ width: `calc(100% - 50px)` }}
                        noWrap
                      >
                        {col2.label}
                      </Typography>
                      <Typography
                        className="cellDefinedName"
                        sx={{ width: `calc(100% - 50px)` }}
                        noWrap
                      >
                        {col2.definedName ? `#${col2.definedName}` : ""}
                      </Typography>
                      {isEdit &&
                        selectedCell?.column === 2 &&
                        selectedCell?.row === index + 1 && (
                          <IconButton
                            sx={{ position: "absolute", right: 8, top: 8 }}
                            aria-label="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              onDeleteCell(col2);
                            }}
                          >
                            <DeleteOutlinedIcon color="error" />
                          </IconButton>
                        )}
                    </Box>
                  ))}
                  <IconButton
                    sx={{
                      backgroundColor: "rgb(277,95,0)",
                      m: 1,
                    }}
                    onClick={onAddColumn2Click}
                  >
                    <AddOutlinedIcon sx={{ color: "white" }} />
                  </IconButton>
                </Box>

                <Box sx={{ width: column3Width }}>
                  {column3Cells.map((col3, index) => (
                    <Box
                      className="rowsColumn2"
                      onClick={() => {
                        onItemClick(3, index);
                      }}
                      sx={{
                        backgroundColor:
                          selectedCell?.column === 3 &&
                          selectedCell?.row === index + 1
                            ? "rgba(227, 95, 0, 0.08)"
                            : "none",
                        borderLeft:
                          column2Cells.length < column3Cells.length &&
                          index >= column2Cells.length
                            ? "1px solid #c4c4c4"
                            : "none",
                      }}
                    >
                      <Typography
                        className="cellLabel"
                        sx={{ width: `calc(100% - 50px)` }}
                        noWrap
                      >
                        {col3.label}
                      </Typography>
                      <Typography
                        className="cellDefinedName"
                        sx={{ width: `calc(100% - 50px)` }}
                        noWrap
                      >
                        {col3.definedName ? `#${col3.definedName}` : ""}
                      </Typography>
                      {isEdit &&
                        selectedCell?.column === 3 &&
                        selectedCell?.row === index + 1 && (
                          <IconButton
                            sx={{ position: "absolute", right: 8, top: 8 }}
                            aria-label="delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              onDeleteCell(col3);
                            }}
                          >
                            <DeleteOutlinedIcon color="error" />
                          </IconButton>
                        )}
                    </Box>
                  ))}
                  <IconButton
                    sx={{
                      backgroundColor: "rgb(277,95,0)",
                      m: 1,
                    }}
                    onClick={onAddColumn3Click}
                  >
                    <AddOutlinedIcon sx={{ color: "white" }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {selectedCell && (
              <FieldSettings
                definedNames={definedNames}
                selectedDefinedNames={selectedDefinedNames}
                cellData={selectedCell}
                isEdit={isEdit}
                onSave={onSaveSettings}
                onCancel={() => {
                  setSelectedCell(null);
                  setIsEdit(false);
                }}
                onDelete={onDeleteCell}
              />
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !!selectedCell ||
              (column1Cells.length === 0 &&
                column2Cells.length === 0 &&
                column3Cells.length === 0)
            }
            onClick={onSaveClick}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
